import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { 
  formatCreatedAtDate,
} from '../../utils/helpers';

// Helper function to get supplier name
const getSupplierName = (supplierId, supplierNamesData) => {
  if (!supplierNamesData || !Array.isArray(supplierNamesData)) return 'Unknown';
  const supplier = supplierNamesData.find(supplier => parseInt(supplier.id) === parseInt(supplierId));
  return supplier ? supplier.name : 'Unknown';
};

// Helper function to get stock name
const getStockName = (stockId, stockNamesData) => {
  if (!stockNamesData || !Array.isArray(stockNamesData)) return 'Unknown';
  const stock = stockNamesData.find(stock => parseInt(stock.id) === parseInt(stockId));
  return stock ? stock.name : 'Unknown';
};

// Helper function to get branch name
const getBranchName = (branchId, branchNamesData) => {
  if (!branchNamesData || !Array.isArray(branchNamesData)) return 'Unknown';
  const branch = branchNamesData.find(branch => parseInt(branch.id) === parseInt(branchId));
  return branch ? `${branch.name}` : 'Unknown';
};

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US').format(amount) + " "+"sh";
};

// Export to CSV with all attributes (using direct download)
export const exportToCSV = async (data, referenceData = {}) => {
  try {
    const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = referenceData;
    
    const fileName = `bar_inventory_${new Date().toISOString().split('T')[0]}.csv`;
    
    const csvData = data.map(item => ({
      'ID': item.id,
      'Stock Date': new Date(item.stock_date).toLocaleDateString(),
      'Supplier': getSupplierName(item.supplier_id, supplierNamesData),
      'Stock Item': getStockName(item.stock_id, stockNamesData),
      'Quantity': item.quantity,
      'Cost Price': `$${formatCurrency(item.cost)}`,
      'Selling Price': `$${formatCurrency(item.selling)}`,
      'Total Cost': `$${formatCurrency(item.quantity * item.cost)}`,
      'Total Selling': `$${formatCurrency(item.quantity * item.selling)}`,
      'Profit Margin': `$${formatCurrency((item.selling - item.cost) * item.quantity)}`,
      'Description': item.description || '',
      'Branch': getBranchName(item.branch_id, branchNamesFilterData),
      'Created Date': formatCreatedAtDate(item.created_at),
      'Updated Date': formatCreatedAtDate(item.updated_at)
    }));

    const csvHeaders = Object.keys(csvData[0]);
    const csvRows = csvData.map(row => 
      csvHeaders.map(header => {
        const value = row[header] || '';
        const escapedValue = String(value).replace(/"/g, '""');
        return escapedValue.includes(',') ? `"${escapedValue}"` : escapedValue;
      }).join(',')
    );
    
    const csvString = [csvHeaders.join(','), ...csvRows].join('\r\n');
    const BOM = '\uFEFF';
    const csvContent = BOM + csvString;
    
    // Create and download without FileSaver
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};

// Export to Excel with all attributes (using direct download)
export const exportToExcel = async (data, referenceData = {}) => {
  try {
    const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = referenceData;
    
    const fileExtension = '.xlsx';
    const fileName = `bar_inventory_${new Date().toISOString().split('T')[0]}${fileExtension}`;
    
    const wsData = [
      ['Stock Date', 'Supplier', 'Stock Item', 'Quantity', 'Cost', 'Selling', 'Branch'],
      ...data.map(item => [
        new Date(item.stock_date).toLocaleDateString(),
        getSupplierName(item.supplier_id, supplierNamesData),
        getStockName(item.stock_id, stockNamesData),
        item.quantity,
        `$${formatCurrency(item.cost)}`,
        `$${formatCurrency(item.selling)}`,
        getBranchName(item.branch_id, branchNamesFilterData)
      ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths
    const wscols = [
      { wch: 15 }, // Stock Date
      { wch: 25 }, // Supplier
      { wch: 30 }, // Stock Item
      { wch: 12 }, // Quantity
      { wch: 15 }, // Cost
      { wch: 15 }, // Selling
      { wch: 20 }  // Branch
    ];
    ws['!cols'] = wscols;

    const wb = { Sheets: { 'Bar Inventory': ws }, SheetNames: ['Bar Inventory'] };
    
    // Use XLSX.writeFile to download directly
    XLSX.writeFile(wb, fileName);
    
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for PDF - UPDATED WITH REQUESTED COLUMNS
const drawInventoryTable = (doc, data, startY, referenceData) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const rowHeight = 12;

  // Column widths for landscape mode
  const colWidths = [30, 40, 50, 25, 25, 25, 40]; // Stock Date, Supplier, Stock Item, Quantity, Cost, Selling, Branch

  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;

  // Check if we need a new page
  if (currentY + neededHeight > pageHeight - 30) {
    doc.addPage('l');
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(25, 118, 210);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');

  const headers = ['Stock Date', 'Supplier', 'Stock Item', 'Quantity', 'Cost', 'Selling', 'Branch'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
  });

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);

  data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4;
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > pageHeight - 30) {
      doc.addPage('l');
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(25, 118, 210);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
      });
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw cell borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    // Draw vertical lines
    let verticalX = margin;
    for (let i = 0; i <= colWidths.length; i++) {
      doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
      if (i < colWidths.length) verticalX += colWidths[i];
    }
    
    // Draw horizontal lines
    doc.line(margin, currentY, pageWidth - margin, currentY);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Export to PDF with specified columns only - UPDATED FOR LANDSCAPE
export const exportToPDF = (data, filters = {}, referenceData = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = referenceData;
      
      // Create PDF in landscape mode
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let currentY = margin;
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(25, 118, 210);
      doc.text("Bar Inventory Report", pageWidth / 2, currentY, { align: 'center' });
      currentY += 15;
      
      // Date and time
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, currentY);
      currentY += 8;
      
      // Total records
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      doc.text(`Total Items: ${data.length}`, margin, currentY);
      
      const totalQuantityText = `Total Quantity: ${formatCurrency(data.reduce((sum, item) => sum + parseInt(item.quantity), 0))}`;
      const totalQuantityWidth = doc.getTextWidth(totalQuantityText);
      doc.text(totalQuantityText, pageWidth - margin - totalQuantityWidth, currentY);
      
      currentY += 8;
      
      // Apply filters text if any
      const filterTexts = [];
      if (filters.supplier_id) {
        filterTexts.push(`Supplier: ${getSupplierName(filters.supplier_id, supplierNamesData)}`);
      }
      if (filters.dateFrom) {
        filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
      }
      if (filters.dateTo) {
        filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
      }
      
      if (filterTexts.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const filtersText = `Filters: ${filterTexts.join(', ')}`;
        const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
        
        filtersLines.forEach(line => {
          if (currentY > pageHeight - 40) {
            doc.addPage('l');
            currentY = margin;
          }
          doc.text(line, margin, currentY);
          currentY += 6;
        });
        
        currentY += 10;
      }
      
      // Prepare table data with REQUESTED COLUMNS ONLY
      const tableData = data.map(item => [
        new Date(item.stock_date).toLocaleDateString(),
        getSupplierName(item.supplier_id, supplierNamesData),
        getStockName(item.stock_id, stockNamesData),
        formatCurrency(item.quantity),
        `$${formatCurrency(item.cost)}`,
        `$${formatCurrency(item.selling)}`,
        getBranchName(item.branch_id, branchNamesFilterData)
      ]);
      
      // Draw the table
      drawInventoryTable(doc, tableData, currentY + 10, referenceData);
      
      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
      
      const fileName = `bar_inventory_report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};